import React, { useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    List,
    ListItemButton,
    Skeleton,
    Stack,
    Typography,
} from '@mui/material';
import { Trans } from 'react-i18next';
import i18n from '../../i18n';
import CommonInput from '../../component/form/common-input.component';
import { ReactComponent as CloseIcon } from '../../static/images/svg/mingcute_close-fill.svg';
import { ReactComponent as CloudDownloadIcon } from '../../static/images/svg/iconamoon_cloud-download-fill.svg';
import { ReactComponent as AutoStoriesIcon } from '../../static/images/svg/auto_stories.svg';
import { ReactComponent as SearchIcon } from '../../static/images/svg/ic_round-search.svg';
import { ReactComponent as PlusIcon } from '../../static/images/svg/ic_round-plus.svg';
import { ReactComponent as SelectAllIcon } from '../../static/images/svg/fluent_select-all-on-16-regular.svg';
import { ReactComponent as CheckBoxCheckedIcon } from '../../static/images/svg/material-symbols_check-box-rounded.svg';
import { ReactComponent as CheckBoxBlankIcon } from '../../static/images/svg/material-symbols_check-box-outline-blank.svg';
import {
    extractCloudDetailCategories,
    extractCloudDetailDishes,
    filterCloudDishesByKeyword,
} from '../../utils/cloud-dish-library';
import { buildRestaurantStaticImageDisplayUrl } from '../../utils/restaurant-static-image-url';
import type { MenuLibraryManagementState } from './menu-library-management-state';
import MenuLibraryLoadCloudFlowDialogs from './menu-library-load-cloud-flow-dialogs.component';
import './scss/menu-library-load-cloud-dish-dialog.style.scss';

/** 筛选区/标题区图标尺寸与颜色（Figma 40 / 32） */
const FILTER_ICON_SX = {
    width: 32,
    height: 32,
    color: 'var(--fill-primary-300)',
    display: 'block',
} as const;

/**
 * 加载弹窗主体骨架：左小类 + 右表格，对齐菜单详情骨架用法（MUI Skeleton wave）。
 */
function LoadCloudDishBodySkeleton() {
    return (
        <Box
            className="menu-library-load-cloud-dish-dialog__body menu-library-load-cloud-dish-dialog__body--skeleton"
            aria-busy="true"
            aria-label={i18n.t('general_text_loading')}
        >
            <Box className="menu-library-load-cloud-dish-dialog__cats">
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <Skeleton variant="rounded" width={32} height={32} sx={{ borderRadius: '8px' }} />
                    <Skeleton variant="rounded" width={120} height={28} sx={{ borderRadius: '8px' }} />
                </Stack>
                <Stack spacing={1}>
                    {[0, 1, 2, 3, 4, 5].map((k) => (
                        <Skeleton
                            key={k}
                            variant="rounded"
                            animation="wave"
                            height={32}
                            sx={{ borderRadius: '16px' }}
                        />
                    ))}
                </Stack>
            </Box>
            <Box className="menu-library-load-cloud-dish-dialog__table-wrap">
                <Stack spacing={1.5} sx={{ p: 1 }}>
                    <Skeleton variant="rounded" animation="wave" height={40} sx={{ borderRadius: '8px' }} />
                    {[0, 1, 2, 3, 4, 5].map((k) => (
                        <Skeleton
                            key={k}
                            variant="rounded"
                            animation="wave"
                            height={80}
                            sx={{ borderRadius: '8px' }}
                        />
                    ))}
                </Stack>
            </Box>
        </Box>
    );
}

/**
 * 加载云端菜品到当前本地菜单弹窗（选云端菜单 / 小类 / 勾选 / add-dishes）。
 * 样式对齐 Figma `9800:49943`。
 */
const MenuLibraryLoadCloudDishDialog: React.FC<{ state: MenuLibraryManagementState }> = observer(
    ({ state }) => {
        // 云端菜单下拉选项
        const menuOptions = useMemo(
            () =>
                state.cloudMenuList.map((m) => ({
                    value: m.id,
                    label: m.main_group_name || m.id,
                })),
            [state.cloudMenuList],
        );

        // 小类列表
        const categories = useMemo(
            () => extractCloudDetailCategories(state.loadCloudMenuDetailRaw),
            [state.loadCloudMenuDetailRaw],
        );

        // 当前小类下的菜品（再按搜索过滤）
        const dishes = useMemo(() => {
            const rows = extractCloudDetailDishes(
                state.loadCloudMenuDetailRaw,
                state.loadCloudSelectedCategoryId,
            );
            return filterCloudDishesByKeyword(rows, state.loadCloudDishSearchKeyword);
        }, [
            state.loadCloudMenuDetailRaw,
            state.loadCloudSelectedCategoryId,
            state.loadCloudDishSearchKeyword,
        ]);

        // 当前可见是否全选
        const allVisibleSelected =
            dishes.length > 0 && dishes.every((d) => state.loadCloudSelectedDishIds.includes(d.id));

        // 本地小类名（底栏文案）
        const localSluName =
            state.detailFirstMenu?.menu_item_slu?.find((s) => s.dmi_slu_id === state.selectedSecondSluId)
                ?.dmi_slu_name || '—';

        // 货币符号（价格列前缀）
        const currency = state.menuLibraryDisplayCurrency || '€';

        const selectedCount = state.loadCloudSelectedDishIds.length;
        const canSubmit =
            selectedCount > 0 && !state.loadCloudAddDishesSubmitting && state.selectedSecondSluId != null;
        // 列表或详情加载中：主体用骨架屏（与菜单详情一致）
        const showBodySkeleton =
            state.cloudMenuListLoading || state.loadCloudMenuDetailLoading;

        /**
         * 格式化价格展示：`€ 9999.99`。
         * @param price - 原始价格
         */
        const formatPrice = (price: string | number | undefined): string => {
            if (price == null || price === '') {
                return '—';
            }
            return `${currency} ${String(price)}`;
        };

        return (
            <>
            <Dialog
                open={state.isLoadCloudDishDialogOpen}
                onClose={() => state.closeLoadCloudDishDialog()}
                maxWidth={false}
                className="menu-library-load-cloud-dish-dialog"
                PaperProps={{ className: 'menu-library-load-cloud-dish-dialog__paper' }}
            >
                <DialogTitle className="menu-library-load-cloud-dish-dialog__title-row">
                    <Box className="menu-library-load-cloud-dish-dialog__title-block">
                        <Box className="menu-library-load-cloud-dish-dialog__title-main">
                            <CloudDownloadIcon
                                className="menu-library-load-cloud-dish-dialog__title-icon"
                                aria-hidden
                            />
                            <Typography variant="h3" component="h2">
                                {i18n.t('menu_library_load_cloud_dialog_title')}
                            </Typography>
                        </Box>
                        <Typography variant="body1" className="menu-library-load-cloud-dish-dialog__subtitle">
                            {i18n.t('menu_library_load_cloud_dialog_subtitle')}
                        </Typography>
                    </Box>
                    <IconButton
                        onClick={() => state.closeLoadCloudDishDialog()}
                        aria-label={i18n.t('general_button_cancel')}
                        className="menu-library-load-cloud-dish-dialog__close"
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent className="menu-library-load-cloud-dish-dialog__content">
                    <Box className="menu-library-load-cloud-dish-dialog__filters">
                        <Box className="menu-library-load-cloud-dish-dialog__filter-field menu-library-load-cloud-dish-dialog__filter-field--menu">
                            <Typography variant="body1" className="menu-library-load-cloud-dish-dialog__filter-label">
                                {i18n.t('menu_library_load_cloud_menu_select')}
                            </Typography>
                            <CommonInput
                                inputType="SELECT"
                                hiddenLabel
                                fullWidth
                                value={state.loadCloudSelectedMenuId || ''}
                                items={menuOptions}
                                className="menu-library-load-cloud-dish-dialog__select"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <AutoStoriesIcon style={FILTER_ICON_SX} aria-hidden />
                                        </InputAdornment>
                                    ),
                                }}
                                onChange={(e) => {
                                    // CommonInput SELECT 走 BaseInput.onChange（事件）
                                    const id = String((e.target as HTMLInputElement).value ?? '');
                                    if (id) {
                                        void state.setLoadCloudSelectedMenuId(id);
                                    }
                                }}
                            />
                        </Box>
                        <Box className="menu-library-load-cloud-dish-dialog__filter-field menu-library-load-cloud-dish-dialog__filter-field--search">
                            <Typography variant="body1" className="menu-library-load-cloud-dish-dialog__filter-label">
                                {i18n.t('menu_library_load_cloud_search_label')}
                            </Typography>
                            <CommonInput
                                inputType="INPUT"
                                hiddenLabel
                                fullWidth
                                value={state.loadCloudDishSearchKeyword}
                                placeholder={i18n.t('menu_library_load_cloud_search_placeholder')}
                                className="menu-library-load-cloud-dish-dialog__search"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon
                                                style={{
                                                    width: 40,
                                                    height: 40,
                                                    color: 'var(--fill-primary-300)',
                                                    display: 'block',
                                                }}
                                                aria-hidden
                                            />
                                        </InputAdornment>
                                    ),
                                }}
                                onChange={(e) =>
                                    state.setLoadCloudDishSearchKeyword(
                                        String((e.target as HTMLInputElement).value ?? ''),
                                    )
                                }
                            />
                        </Box>
                    </Box>

                    {showBodySkeleton ? (
                        <LoadCloudDishBodySkeleton />
                    ) : (
                    <Box className="menu-library-load-cloud-dish-dialog__body">
                        <Box className="menu-library-load-cloud-dish-dialog__cats" component="nav">
                            <Box className="menu-library-load-cloud-dish-dialog__cats-title-row">
                                <AutoStoriesIcon
                                    className="menu-library-load-cloud-dish-dialog__cats-icon"
                                    aria-hidden
                                />
                                <Typography
                                    variant="h4"
                                    component="h3"
                                    className="menu-library-load-cloud-dish-dialog__cats-title"
                                >
                                    {i18n.t('menu_store_detail_subcategories')}
                                </Typography>
                            </Box>
                            {categories.length === 0 ? (
                                <Typography variant="body2">
                                    {i18n.t('menu_library_load_cloud_no_dishes_in_menu')}
                                </Typography>
                            ) : (
                                <List disablePadding className="menu-library-load-cloud-dish-dialog__cats-list">
                                    {categories.map((c) => {
                                        const selected = state.loadCloudSelectedCategoryId === c.id;
                                        return (
                                            <ListItemButton
                                                key={c.id}
                                                selected={selected}
                                                className="menu-library-load-cloud-dish-dialog__cat-item"
                                                onClick={() => state.setLoadCloudSelectedCategoryId(c.id)}
                                            >
                                                <Typography
                                                    variant="body1"
                                                    className="menu-library-load-cloud-dish-dialog__cat-label"
                                                >
                                                    {`·${c.name}`}
                                                </Typography>
                                            </ListItemButton>
                                        );
                                    })}
                                </List>
                            )}
                        </Box>

                        <Box className="menu-library-load-cloud-dish-dialog__table-wrap">
                            {dishes.length === 0 ? (
                                <Typography variant="body1">
                                    {i18n.t('menu_library_load_cloud_no_dishes_in_category')}
                                </Typography>
                            ) : (
                                <Box className="menu-library-load-cloud-dish-dialog__table" role="table">
                                    <Box className="menu-library-load-cloud-dish-dialog__thead" role="row">
                                        <Typography
                                            variant="body1"
                                            role="columnheader"
                                            className="menu-library-load-cloud-dish-dialog__th-name"
                                        >
                                            {/* Figma 表头「菜品名称」对应缩略图列 */}
                                            {i18n.t('menu_library_load_cloud_col_name')}
                                        </Typography>
                                        <Typography variant="body1" role="columnheader">
                                            {i18n.t('menu_library_load_cloud_col_name1')}
                                        </Typography>
                                        <Typography variant="body1" role="columnheader">
                                            {i18n.t('menu_library_load_cloud_col_name2')}
                                        </Typography>
                                        <Typography variant="body1" role="columnheader">
                                            {i18n.t('menu_library_load_cloud_col_price')}
                                        </Typography>
                                        <Box
                                            role="columnheader"
                                            className="menu-library-load-cloud-dish-dialog__check-col"
                                        >
                                            <Typography variant="body1" component="span">
                                                {i18n.t('menu_library_load_cloud_select_all')}
                                            </Typography>
                                            <IconButton
                                                size="small"
                                                className="menu-library-load-cloud-dish-dialog__select-all-btn"
                                                aria-label={i18n.t('menu_library_load_cloud_select_all')}
                                                aria-pressed={allVisibleSelected}
                                                onClick={() =>
                                                    state.setLoadCloudDishSelectionForIds(
                                                        dishes.map((d) => d.id),
                                                        !allVisibleSelected,
                                                    )
                                                }
                                            >
                                                <SelectAllIcon aria-hidden />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                    {dishes.map((d) => {
                                        const checked = state.loadCloudSelectedDishIds.includes(d.id);
                                        // 展示用缩略图：接口 `dish_image` 完整 URL
                                        const thumbSrc = buildRestaurantStaticImageDisplayUrl(d.image);
                                        // 无障碍标签：优先名称1/名称2（与表列一致）
                                        const dishAriaLabel = d.name1 || d.name2 || d.name;
                                        return (
                                            <Box
                                                key={d.id}
                                                className={
                                                    checked
                                                        ? 'menu-library-load-cloud-dish-dialog__trow menu-library-load-cloud-dish-dialog__trow--selected'
                                                        : 'menu-library-load-cloud-dish-dialog__trow'
                                                }
                                                role="row"
                                            >
                                                <Box
                                                    role="cell"
                                                    className="menu-library-load-cloud-dish-dialog__thumb-cell"
                                                >
                                                    {thumbSrc ? (
                                                        <img
                                                            className="menu-library-load-cloud-dish-dialog__thumb"
                                                            src={thumbSrc}
                                                            alt=""
                                                        />
                                                    ) : (
                                                        <Box
                                                            className="menu-library-load-cloud-dish-dialog__thumb menu-library-load-cloud-dish-dialog__thumb--placeholder"
                                                            aria-hidden
                                                        />
                                                    )}
                                                </Box>
                                                <Typography
                                                    variant="body1"
                                                    role="cell"
                                                    className="menu-library-load-cloud-dish-dialog__cell-ellipsis"
                                                >
                                                    {/* Figma「菜品名称1」列：接口 item_name1 */}
                                                    {d.name1 || '—'}
                                                </Typography>
                                                <Typography
                                                    variant="body1"
                                                    role="cell"
                                                    className="menu-library-load-cloud-dish-dialog__cell-ellipsis"
                                                >
                                                    {/* Figma「菜品名称2」列：接口 item_name2 */}
                                                    {d.name2 || '—'}
                                                </Typography>
                                                <Typography
                                                    variant="body1"
                                                    role="cell"
                                                    className="menu-library-load-cloud-dish-dialog__cell-price"
                                                >
                                                    {formatPrice(d.price)}
                                                </Typography>
                                                <Box
                                                    role="cell"
                                                    className="menu-library-load-cloud-dish-dialog__check-col"
                                                >
                                                    <Checkbox
                                                        checked={checked}
                                                        icon={
                                                            <CheckBoxBlankIcon className="menu-library-load-cloud-dish-dialog__cb-icon" />
                                                        }
                                                        checkedIcon={
                                                            <CheckBoxCheckedIcon className="menu-library-load-cloud-dish-dialog__cb-icon" />
                                                        }
                                                        onChange={() => state.toggleLoadCloudDishSelection(d.id)}
                                                        inputProps={{
                                                            'aria-label': dishAriaLabel,
                                                        }}
                                                    />
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            )}
                        </Box>
                    </Box>
                    )}

                    <Box className="menu-library-load-cloud-dish-dialog__footer">
                        <Typography variant="h3" className="menu-library-load-cloud-dish-dialog__footer-text">
                            <Trans
                                i18nKey="menu_library_load_cloud_footer_summary"
                                values={{ count: selectedCount, sluName: localSluName }}
                                components={{
                                    highlight: (
                                        <Typography
                                            component="span"
                                            variant="h3"
                                            className="menu-library-load-cloud-dish-dialog__emphasis"
                                        />
                                    ),
                                }}
                            />
                        </Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={!canSubmit}
                            className="menu-library-load-cloud-dish-dialog__submit"
                            startIcon={<PlusIcon aria-hidden />}
                            onClick={() => state.openLoadCloudConfirmDialog()}
                        >
                            <Typography component="span" variant="h4">
                                {i18n.t('menu_library_load_cloud_add_local_menu')}
                            </Typography>
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>
            <MenuLibraryLoadCloudFlowDialogs state={state} />
        </>
        );
    },
);

export default MenuLibraryLoadCloudDishDialog;
